import axios, {AxiosError, AxiosInstance, AxiosRequestConfig, AxiosResponse} from "axios"
import {store} from "helpers"
import {get} from "lodash"
import {coreSocket} from "services/index"
import {emitEvent} from "helpers/eventHelper"

export enum Header {
    Authorization = "authorization",
    ProfileId = "x-profile-id"
}

export const EVENT_API_REQUEST_UNAUTHORIZED = "EVENT_API_REQUEST_UNAUTHORIZED"

class BaseService {
    protected axios: AxiosInstance
    public static authToken: string
    public static profileId: number
    private static RETRIES = 1

    constructor(private baseURL = process.env.REACT_APP_EDULAR_CORE_API_BASE_URL) {
        this.axios = axios.create({
            timeout: 180 * 1000,
            baseURL
        })
        this.addInterceptors()
    }

    protected addInterceptors() {
        this.axios.interceptors.request.use(this.onRequest, this.onRequestError)
        this.axios.interceptors.response.use(this.onResponse, this.onResponseError)
    }

    public static getBaseUrl = () => {
        return process.env.REACT_APP_EDULAR_CORE_API_BASE_URL
    }

    public changeProfileId = () => {
        const user = store.get("user")
        if (user) {
            const profile = user.profiles.find((profile) => !profile.isAccessDisabled)
            if (!profile) {
                store.clearAll()
                window.location.href = "/"
            }
            BaseService.setProfileId(profile.id)
        }
    }

    protected setHeaderFromParams({headers, signal, responseType, baseURL, ...params}: any) {
        return {
            headers: {
                ...headers
            },
            signal,
            responseType,
            baseURL,
            params
        }
    }

    public static setAuthToken(token) {
        BaseService.authToken = token
    }

    public static setProfileId(profileId: number) {
        BaseService.profileId = profileId
        coreSocket.setProfileId(profileId)
        coreSocket.login()
    }

    async get(url, params = {}) {
        const config = this.setHeaderFromParams(params)
        const response = await this.axios.get(url, config)
        return response
    }

    async post(url, data, params = {}) {
        const config = this.setHeaderFromParams(params)
        const response = await this.axios.post(url, data, config)
        return response
    }

    async put(url, data, params = {}) {
        const config = this.setHeaderFromParams(params)
        const response = await this.axios.put(url, data, config)
        return response
    }

    async delete(url, params = {}) {
        const config = this.setHeaderFromParams(params)
        const response = await this.axios.delete(url, config)
        return response
    }

    private onRequest = async (config: AxiosRequestConfig): Promise<AxiosRequestConfig> => {
        // Do something before request is sent
        config["retryCount"] = config["retryCount"] || 0

        if (!config.headers["Content-Type"]) {
            config.headers["Content-Type"] = "application/json"
        }
        if (!config.headers[Header.Authorization] && BaseService.authToken) {
            config.headers[Header.Authorization] = `Bearer ${BaseService.authToken}`
        }
        if (!config.headers[Header.ProfileId] && BaseService.profileId) {
            config.headers[Header.ProfileId] = BaseService.profileId
        }
        return config
    }

    private onRequestError = (error: AxiosError) => {
        // Do something with request error

        const {config, request} = error
        if (request) {
            // The request was made but no response was received
            // `error.request` is an instance of XMLHttpRequest in the browser
            // and an instance of http.ClientRequest in node.js
            console.error("api request error", error.code, error.message, request)
        } else {
            // Something happened in setting up the request that triggered an Error
            console.error("api request error", error.code, error.message)
        }

        // retry API request
        if (config["retryCount"] < BaseService.RETRIES) {
            config["retryCount"] += 1
            return this.axios.request(config)
        }

        throw error
    }

    private onResponse = async (response: AxiosResponse): Promise<AxiosResponse> => {
        // Any status code that lie within the range of 2xx cause this function to trigger
        // Do something with response data
        return response
    }

    private onResponseError = async (error: AxiosError): Promise<void> => {
        // Do something with response error

        const {config, response} = error
        // Any status codes that falls outside the range of 2xx cause this function to trigger
        if (response) {
            // The request was made and the server responded with a status code
            // that falls out of the range of 2xx

            const errorCode = get(response, "data.code")
            const user = store.get("user")
            if (errorCode === "access_disabled" && user) {
                store.clearAll()
                window.location.reload()
                return
            }
            if (errorCode === "profile_access_disabled" && user) {
                this.changeProfileId()
                window.location.reload()
                return
            }
            if (
                response.status === 401 &&
                !config.url?.includes("/auth/v2/normal-sign-in") &&
                !config.url?.includes("/auth/v2/logout")
            ) {
                emitEvent(EVENT_API_REQUEST_UNAUTHORIZED, {})
                return
            }
            const messageError = get(response, "data.errors[0].message", "") || get(response, "data.message", "") || ""

            if (response.status === 400) {
                throw new Error(messageError)
            }

            throw new Error(`Server Internal Error. ${messageError}`)
        }

        throw error
    }
}

export default BaseService
